import { defineResource, standardActions, taxBranch } from '@tnn/core'
import { z } from 'zod'
import { businessTypes } from '../party'

/** How long what is good to know about a contact may be: room for two merged into one */
export const contactNotesMax = 2000

/**
 * Senders and receivers. Waybills copy the details they need at booking time;
 * this address book is what the name field suggests from. Its notes are
 * what is good to know about them (สิ่งที่ควรรู้): the bill and the delivery run show them
 * as they are now, never copied (contact-match.ts). Duplicates are merged into one (`merge`).
 */
export const contact = defineResource({
  app: 'tms',
  name: 'contact',
  path: 'contacts',
  schema: z.object({
    name: z.string().trim().min(1).max(200),
    /** The name receipts are made out to, as the company is registered (บริษัท … จำกัด), when bills write it otherwise */
    registeredName: z.string().trim().max(200).nullish(),
    phone: z.string().trim().max(100).nullish(),
    idCard: z.string().trim().max(30).nullish(),
    notes: z.string().max(contactNotesMax).nullish(),
    address: z.string().max(500).nullish(),
    subdistrict: z.string().trim().max(100).nullish(),
    district: z.string().trim().max(100).nullish(),
    province: z.string().trim().max(100).nullish(),
    postcode: z.string().trim().max(10).nullish(),
    taxId: z
      .string()
      .regex(/^\d{13}$/)
      .nullish(),
    /** A company's tax branch: 00000 = head office, otherwise its number (kept from a receipt in its name) */
    branch: z.string().trim().max(10).nullish(),
    businessType: z.enum(businessTypes).nullish(),
  }),
  fields: {
    name: { search: true, list: { priority: 1 }, form: { span: 6 }, import: { aliases: ['ชื่อ', 'ชื่อผู้ติดต่อ'] } },
    phone: {
      type: 'phone',
      warnTaken: true,
      search: true,
      list: { width: 180, priority: 1 },
      form: { span: 6 },
      import: { aliases: ['เบอร์โทร', 'โทรศัพท์'] },
    },
    registeredName: { search: true, list: false, form: { span: 6 } },
    idCard: { list: false, form: { span: 6 } },
    // Shown on every bill made out to them, so it sits with who they are
    notes: { type: 'textarea', form: { span: 12 }, import: { aliases: ['หมายเหตุ'] } },
    // One sender in the book once: the form names whoever has the tax ID (and branch) or a number already
    taxId: { type: 'taxId', list: { priority: 3 }, form: { span: 6 }, warnTaken: { with: ['branch'] } },
    branch: { list: false, form: { span: 6 }, import: { aliases: ['สาขา'], value: taxBranch } },
    address: { type: 'textarea', list: false, form: { span: 12, section: 'address' } },
    province: {
      filter: true,
      list: { width: 150, priority: 2 },
      form: { span: 12, section: 'address', custom: true },
      import: { aliases: ['จังหวัด'], header: 'form.province' },
    },
    district: { list: { priority: 3 }, form: { hidden: true } },
    subdistrict: { list: false, form: { hidden: true } },
    postcode: { list: false, form: { hidden: true } },
    businessType: {
      optionLabels: 'tms:businessType',
      filter: true,
      list: { width: 180, priority: 2 },
      form: { span: 6, section: 'other' },
      import: { aliases: ['ประเภทธุรกิจ', 'ประเภทร้าน'] },
    },
  },
  title: 'name',
  columns: ['name', 'phone', 'businessType', 'province', 'district', 'taxId'],
  sections: ['main', 'address', 'other'],
  defaultSort: 'name',
  actions: [...standardActions, 'merge'],
  card: { title: 'name', subtitle: 'phone', meta: ['province'] },
})
